import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Plus, Search, Edit2, Trash2, Upload, AlertTriangle, CheckCircle, Download, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InventoryPage() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({});
  const { toast } = useToast();
  const { user } = useAuth();

  // Bulk import state
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [fileData, setFileData] = useState(null); // Store file as ArrayBuffer
  const [importing, setImporting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const fileInputRef = useRef(null);

  // Inventory update state
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  const [inventoryFile, setInventoryFile] = useState(null);
  const [updatingInventory, setUpdatingInventory] = useState(false);
  const [inventoryResult, setInventoryResult] = useState(null);
  const inventoryFileInputRef = useRef(null);

  // Color Parent update state
  const [colorParentDialogOpen, setColorParentDialogOpen] = useState(false);
  const [colorParentFile, setColorParentFile] = useState(null);
  const [updatingColorParent, setUpdatingColorParent] = useState(false);
  const [colorParentResult, setColorParentResult] = useState(null);
  const colorParentFileInputRef = useRef(null);

  // Bulk Edit state
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [bulkEditFile, setBulkEditFile] = useState(null);
  const [bulkEditFileData, setBulkEditFileData] = useState(null);
  const [bulkEditPreviewLoading, setBulkEditPreviewLoading] = useState(false);
  const [bulkEditPreview, setBulkEditPreview] = useState(null);
  const [bulkEditApplying, setBulkEditApplying] = useState(false);
  const [bulkEditResult, setBulkEditResult] = useState(null);
  const bulkEditFileInputRef = useRef(null);

  useEffect(() => {
    fetchProducts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchTerm) {
      const filtered = products.filter((p) =>
        p.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products);
    }
    setCurrentPage(1);
  }, [searchTerm, products]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const paginatedProducts = filteredProducts.slice(pageStart, pageStart + pageSize);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load products',
        variant: 'destructive',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, formData);
        toast({ title: 'Success', description: 'Product updated successfully' });
      } else {
        await axios.post(`${API}/products`, formData);
        toast({ title: 'Success', description: 'Product created successfully' });
      }
      setIsDialogOpen(false);
      setEditingProduct(null);
      setFormData({});
      fetchProducts();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save product',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/products/${id}`);
      toast({ title: 'Success', description: 'Product deleted successfully' });
      fetchProducts();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete product',
        variant: 'destructive',
      });
    }
  };

  const openDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        sku: product.sku,
        name: product.name,
        parent_sku: product.parent_sku || '',
        color_parent: product.color_parent || '',
        category: product.category || 'Single',
        vendor: product.vendor || '',
        purchase_price: product.purchase_price || 0,
        inventory: product.inventory || 0,
        unit_weight: product.unit_weight || 0,
        moq: product.moq || '',
        reorder_quantity: product.reorder_quantity || '',
        manufacturer_lead_time: product.manufacturer_lead_time || '',
        shipping_lead_time: product.shipping_lead_time || '',
        launch_date: product.launch_date || '',
        launch_dates: product.launch_dates || {},
        initial_stock: product.initial_stock || {},
      });
    } else {
      setEditingProduct(null);
      setFormData({
        sku: '',
        name: '',
        parent_sku: '',
        color_parent: '',
        category: 'Single',
        vendor: '',
        purchase_price: 0,
        inventory: 0,
        unit_weight: 0,
        moq: '',
        reorder_quantity: '',
        manufacturer_lead_time: '',
        shipping_lead_time: '',
        launch_date: '',
        launch_dates: {},
        initial_stock: {},
      });
    }
    setIsDialogOpen(true);
  };

  // Bulk Import Functions
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setBulkImportFile(file);
    setPreviewData(null);
    setImportResult(null);
    setUpdateExisting(false);
    setPreviewError(null);
    setPreviewLoading(true);
    
    // Read file into memory immediately to prevent stale reference
    try {
      const arrayBuffer = await file.arrayBuffer();
      setFileData({ buffer: arrayBuffer, name: file.name, type: file.type });
    } catch (err) {
      console.error('Error reading file:', err);
    }
    
    // Get preview
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/products/bulk-import/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setPreviewData(response.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to preview file';
      setPreviewError(errorMsg);
      toast({
        title: 'Preview Error',
        description: errorMsg,
        variant: 'destructive',
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleBulkImport = async () => {
    // Use stored file data (ArrayBuffer) to create a fresh file object
    if (!fileData || !fileData.buffer) {
      toast({
        title: 'Error',
        description: 'File not found. Please select a file again.',
        variant: 'destructive',
      });
      setPreviewData(null);
      setBulkImportFile(null);
      setFileData(null);
      return;
    }
    
    setImporting(true);
    try {
      // Create a new File object from stored ArrayBuffer
      const blob = new Blob([fileData.buffer], { type: fileData.type || 'application/octet-stream' });
      const freshFile = new File([blob], fileData.name, { type: fileData.type });
      
      const formData = new FormData();
      formData.append('file', freshFile);
      formData.append('update_existing', updateExisting ? 'true' : 'false');
      
      const response = await axios.post(`${API}/products/bulk-import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      if (response.data && response.data.message) {
        setImportResult(response.data);
        fetchProducts(); // Refresh the product list
        
        toast({
          title: 'Import Complete',
          description: `${response.data.created} products added, ${response.data.updated} updated`,
        });
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Import error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to import products.';
      
      setPreviewError(errorMsg);
      setPreviewData(null);
      
      toast({
        title: 'Import Failed',
        description: errorMsg,
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
    }
  };

  const closeBulkImportDialog = () => {
    setBulkImportDialogOpen(false);
    setBulkImportFile(null);
    setFileData(null);
    setPreviewData(null);
    setImportResult(null);
    setUpdateExisting(false);
    setPreviewError(null);
    setPreviewLoading(false);
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csvContent = `SKU,Name,Parent SKU,Color Parent,Category,Vendor,Purchase Price,Inventory,Unit Weight,MOQ,Reorder Quantity,Manufacturer Lead Time,Shipping Lead Time,Amazon UAE Launch Date,Amazon KSA Launch Date,Amazon AU Launch Date,Noon UAE Launch Date,Amazon UAE Initial Stock,Amazon KSA Initial Stock,Amazon AU Initial Stock,Noon UAE Initial Stock
SAMPLE-001,Sample Product One,PARENT-001,PARENT-001-RED,Single,Vendor Name,100.00,50,0.25,10,20,7,3,01-04-2026,01-04-2026,,,30,30,,
SAMPLE-002,Sample Product Two,PARENT-001,PARENT-001-RED,Single,Vendor Name,150.00,30,0.30,5,15,7,3,,15-05-2026,,,,20,,`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Inventory Update Functions
  const handleInventoryFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setInventoryFile(file);
    setInventoryResult(null);
  };

  const handleInventoryUpdate = async () => {
    if (!inventoryFile) {
      toast({
        title: 'Error',
        description: 'Please select a file',
        variant: 'destructive',
      });
      return;
    }

    setUpdatingInventory(true);
    try {
      const formData = new FormData();
      formData.append('file', inventoryFile);

      const response = await axios.post(`${API}/products/bulk-update-inventory`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setInventoryResult(response.data);
      toast({
        title: 'Success',
        description: `Updated inventory for ${response.data.updated} products`,
      });
      fetchProducts();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update inventory',
        variant: 'destructive',
      });
    } finally {
      setUpdatingInventory(false);
    }
  };

  const downloadInventoryTemplate = async () => {
    try {
      const response = await axios.get(`${API}/products/download-inventory-template`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'inventory_update_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to download template',
        variant: 'destructive',
      });
    }
  };

  const closeInventoryDialog = () => {
    setInventoryDialogOpen(false);
    setInventoryFile(null);
    setInventoryResult(null);
    if (inventoryFileInputRef.current) {
      inventoryFileInputRef.current.value = '';
    }
  };

  // Color Parent Update Functions
  const handleColorParentFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setColorParentFile(file);
    setColorParentResult(null);
  };

  const handleColorParentUpdate = async () => {
    if (!colorParentFile) {
      toast({
        title: 'Error',
        description: 'Please select a file',
        variant: 'destructive',
      });
      return;
    }

    setUpdatingColorParent(true);
    try {
      const formData = new FormData();
      formData.append('file', colorParentFile);

      const response = await axios.post(`${API}/products/bulk-update-parents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setColorParentResult(response.data);
      toast({
        title: 'Success',
        description: `Updated parents for ${response.data.updated} products`,
      });
      fetchProducts();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update parents',
        variant: 'destructive',
      });
    } finally {
      setUpdatingColorParent(false);
    }
  };

  const downloadColorParentTemplate = async () => {
    try {
      const response = await axios.get(`${API}/products/download-parents-template`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `parents_template_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to download template',
        variant: 'destructive',
      });
    }
  };

  const closeColorParentDialog = () => {
    setColorParentDialogOpen(false);
    setColorParentFile(null);
    setColorParentResult(null);
    if (colorParentFileInputRef.current) {
      colorParentFileInputRef.current.value = '';
    }
  };

  // Bulk Edit Functions
  const downloadAllProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/download-all`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({ title: 'Download started', description: 'Products file downloaded successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download products', variant: 'destructive' });
    }
  };

  const handleBulkEditFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setBulkEditFile(file);
    setBulkEditPreview(null);
    setBulkEditResult(null);
    setBulkEditPreviewLoading(true);

    // Store file data to prevent stale reference
    try {
      const arrayBuffer = await file.arrayBuffer();
      setBulkEditFileData({ buffer: arrayBuffer, name: file.name, type: file.type });
    } catch (err) {
      console.error('Error reading file:', err);
    }

    // Get preview
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/products/bulk-edit-preview`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkEditPreview(response.data);
    } catch (error) {
      toast({
        title: 'Preview Error',
        description: error.response?.data?.detail || 'Failed to preview changes',
        variant: 'destructive',
      });
    } finally {
      setBulkEditPreviewLoading(false);
    }
  };

  const handleBulkEditApply = async () => {
    if (!bulkEditFileData?.buffer) {
      toast({ title: 'Error', description: 'File not found. Please select again.', variant: 'destructive' });
      return;
    }

    setBulkEditApplying(true);
    try {
      const blob = new Blob([bulkEditFileData.buffer], { type: bulkEditFileData.type || 'application/octet-stream' });
      const freshFile = new File([blob], bulkEditFileData.name, { type: bulkEditFileData.type });
      const fd = new FormData();
      fd.append('file', freshFile);

      const response = await axios.post(`${API}/products/bulk-edit-apply`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setBulkEditResult(response.data);
      toast({ title: 'Success', description: `Updated ${response.data.updated} products` });
      fetchProducts();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to apply edits',
        variant: 'destructive',
      });
    } finally {
      setBulkEditApplying(false);
    }
  };

  const closeBulkEditDialog = () => {
    setBulkEditDialogOpen(false);
    setBulkEditFile(null);
    setBulkEditFileData(null);
    setBulkEditPreview(null);
    setBulkEditResult(null);
    setBulkEditPreviewLoading(false);
    setBulkEditApplying(false);
    if (bulkEditFileInputRef.current) {
      bulkEditFileInputRef.current.value = '';
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight font-heading" data-testid="page-title">Master Inventory</h1>
            <p className="text-muted-foreground mt-2">{products.length} products in system</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={downloadAllProducts}
              data-testid="download-all-products-button"
            >
              <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Download All
            </Button>
            <Button
              variant="outline"
              onClick={() => setBulkEditDialogOpen(true)}
              data-testid="bulk-edit-button"
            >
              <Edit2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Edit via File
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setColorParentDialogOpen(true)}
              data-testid="update-parents-button"
            >
              <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Update Parents
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setInventoryDialogOpen(true)}
              data-testid="update-inventory-button"
            >
              <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Update Inventory
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setBulkImportDialogOpen(true)}
              data-testid="bulk-import-button"
            >
              <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Bulk Import
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => openDialog()} data-testid="add-product-button">
                  <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Add Product
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle data-testid="dialog-title">{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">SKU *</label>
                    <Input
                      data-testid="input-sku"
                      value={formData.sku || ''}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      required
                      disabled={!!editingProduct}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Product Name *</label>
                    <Input
                      data-testid="input-name"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Parent SKU</label>
                    <Input
                      data-testid="input-parent-sku"
                      value={formData.parent_sku || ''}
                      onChange={(e) => setFormData({ ...formData, parent_sku: e.target.value })}
                      placeholder="e.g., RSO1P"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Color Parent</label>
                    <Input
                      data-testid="input-color-parent"
                      value={formData.color_parent || ''}
                      onChange={(e) => setFormData({ ...formData, color_parent: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Vendor</label>
                    <Input
                      data-testid="input-vendor"
                      value={formData.vendor || ''}
                      onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Purchase Price</label>
                    <Input
                      data-testid="input-purchase-price"
                      type="number"
                      step="0.01"
                      value={formData.purchase_price || 0}
                      onChange={(e) => setFormData({ ...formData, purchase_price: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Inventory</label>
                    <Input
                      data-testid="input-inventory"
                      type="number"
                      value={formData.inventory || 0}
                      onChange={(e) => setFormData({ ...formData, inventory: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Unit Weight (kg)</label>
                    <Input
                      data-testid="input-unit-weight"
                      type="number"
                      step="0.01"
                      value={formData.unit_weight || 0}
                      onChange={(e) => setFormData({ ...formData, unit_weight: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">MOQ</label>
                    <Input
                      data-testid="input-moq"
                      type="number"
                      value={formData.moq || ''}
                      onChange={(e) => setFormData({ ...formData, moq: parseInt(e.target.value) || null })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Reorder Quantity</label>
                    <Input
                      data-testid="input-reorder-quantity"
                      type="number"
                      value={formData.reorder_quantity || ''}
                      onChange={(e) => setFormData({ ...formData, reorder_quantity: parseInt(e.target.value) || null })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Manufacturer Lead Time (days)</label>
                    <Input
                      data-testid="input-manufacturer-lead-time"
                      type="number"
                      value={formData.manufacturer_lead_time || ''}
                      onChange={(e) => setFormData({ ...formData, manufacturer_lead_time: parseInt(e.target.value) || null })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Shipping Lead Time (days)</label>
                    <Input
                      data-testid="input-shipping-lead-time"
                      type="number"
                      value={formData.shipping_lead_time || ''}
                      onChange={(e) => setFormData({ ...formData, shipping_lead_time: parseInt(e.target.value) || null })}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Launch Date (optional)</label>
                  <Input
                    data-testid="input-launch-date"
                    type="date"
                    value={formData.launch_date || ''}
                    onChange={(e) => setFormData({ ...formData, launch_date: e.target.value || null })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Products launched within the review period are excluded from discontinue suggestions.</p>
                </div>
                <div className="border border-border rounded-sm p-3 bg-muted/20">
                  <label className="block text-sm font-medium mb-1">International Launch Dates (optional)</label>
                  <p className="text-xs text-muted-foreground mb-3">Used by the Launch Tracker. The earliest date among a color parent's SKUs is taken as its launch date for that market.</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'amazon_uae', label: 'Amazon UAE' },
                      { key: 'amazon_ksa', label: 'Amazon KSA' },
                      { key: 'amazon_au', label: 'Amazon AU' },
                      { key: 'noon_uae', label: 'Noon UAE' },
                    ].map((m) => (
                      <div key={m.key}>
                        <label className="block text-xs text-muted-foreground mb-1">{m.label}</label>
                        <Input
                          data-testid={`input-launch-${m.key}`}
                          type="date"
                          value={(formData.launch_dates && formData.launch_dates[m.key]) || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            launch_dates: { ...(formData.launch_dates || {}), [m.key]: e.target.value || null },
                          })}
                        />
                      </div>
                    ))}
                  </div>
                  <label className="block text-sm font-medium mt-4 mb-1">Initial Launch Stock (optional)</label>
                  <p className="text-xs text-muted-foreground mb-3">Units sent to each international market at launch (per SKU). The Launch Tracker sums these across a color parent's SKUs for the 70% sell-through check.</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'amazon_uae', label: 'Amazon UAE' },
                      { key: 'amazon_ksa', label: 'Amazon KSA' },
                      { key: 'amazon_au', label: 'Amazon AU' },
                      { key: 'noon_uae', label: 'Noon UAE' },
                    ].map((m) => (
                      <div key={m.key}>
                        <label className="block text-xs text-muted-foreground mb-1">{m.label}</label>
                        <Input
                          data-testid={`input-stock-${m.key}`}
                          type="number"
                          min="0"
                          placeholder="optional"
                          value={(formData.initial_stock && formData.initial_stock[m.key] != null) ? formData.initial_stock[m.key] : ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            initial_stock: { ...(formData.initial_stock || {}), [m.key]: e.target.value === '' ? null : parseInt(e.target.value) },
                          })}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" data-testid="save-product-button">
                    {editingProduct ? 'Update Product' : 'Create Product'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    data-testid="cancel-button"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <Input
              data-testid="search-input"
              placeholder="Search by SKU or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="border border-border rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="products-table">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left p-3 text-xs uppercase tracking-widest font-medium">SKU</th>
                  <th className="text-left p-3 text-xs uppercase tracking-widest font-medium">Product Name</th>
                  <th className="text-left p-3 text-xs uppercase tracking-widest font-medium">Vendor</th>
                  <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">Stock</th>
                  <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">Price</th>
                  <th className="text-center p-3 text-xs uppercase tracking-widest font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length > 0 ? (
                  paginatedProducts.map((product) => (
                    <tr key={product.id} data-testid={`product-row-${product.sku}`} className="border-b border-border hover:bg-muted/30 transition-colors">
                      <td className="p-3 font-mono text-sm font-medium">{product.sku}</td>
                      <td className="p-3 text-sm">{product.name}</td>
                      <td className="p-3 text-sm text-muted-foreground">{product.vendor || 'N/A'}</td>
                      <td className="p-3 text-right font-mono text-sm">
                        <span className={product.inventory < 50 ? 'text-destructive font-medium' : ''}>
                          {product.inventory}
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono text-sm">₹{product.purchase_price.toFixed(2)}</td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openDialog(product)}
                            data-testid={`edit-${product.sku}`}
                            className="p-1 hover:bg-muted rounded-sm transition-colors"
                          >
                            <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                          {user?.role === 'admin' && (
                            <button
                              onClick={() => handleDelete(product.id)}
                              data-testid={`delete-${product.sku}`}
                              className="p-1 hover:bg-destructive/10 text-destructive rounded-sm transition-colors"
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="p-12 text-center">
                      <div
                        className="flex flex-col items-center justify-center opacity-50"
                        style={{
                          backgroundImage: 'url(https://images.unsplash.com/photo-1601926299866-6a5c9bfa6be0?crop=entropy&cs=srgb&fm=jpg&q=85)',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          filter: 'grayscale(100%)',
                          minHeight: '200px',
                        }}
                      >
                        <div className="bg-background/90 p-6 rounded-sm">
                          <p className="text-muted-foreground" data-testid="no-products-message">
                            {searchTerm ? 'No products match your search' : 'No products yet. Add your first product to get started.'}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {filteredProducts.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="pagination-controls">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span data-testid="pagination-range">
                Showing {pageStart + 1}–{Math.min(pageStart + pageSize, filteredProducts.length)} of {filteredProducts.length}
              </span>
              <div className="flex items-center gap-2">
                <span>Rows:</span>
                <select
                  data-testid="page-size-select"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  className="h-8 rounded-sm border border-border bg-background px-2 text-sm"
                >
                  {[25, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" data-testid="page-first" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                <ChevronsLeft className="w-4 h-4" strokeWidth={1.5} />
              </Button>
              <Button variant="outline" size="sm" data-testid="page-prev" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
                <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
              </Button>
              <span className="px-3 text-sm font-medium" data-testid="page-indicator">Page {currentPage} of {totalPages}</span>
              <Button variant="outline" size="sm" data-testid="page-next" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>
                <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
              </Button>
              <Button variant="outline" size="sm" data-testid="page-last" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
                <ChevronsRight className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        )}

        {/* Bulk Import Dialog */}
        <Dialog open={bulkImportDialogOpen} onOpenChange={closeBulkImportDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle data-testid="bulk-import-dialog-title">Bulk Import Products</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {!importResult ? (
                <>
                  <div className="bg-muted/30 border border-border rounded-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium">Supported Columns:</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={downloadTemplate}
                        data-testid="download-template-button"
                      >
                        <Download className="w-3 h-3 mr-1" strokeWidth={1.5} />
                        Download Template
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-sm text-muted-foreground">
                      <span>• SKU (required)</span>
                      <span>• Name</span>
                      <span>• Parent SKU</span>
                      <span>• Category</span>
                      <span>• Vendor</span>
                      <span>• Purchase Price</span>
                      <span>• Inventory</span>
                      <span>• Unit Weight</span>
                      <span>• MOQ</span>
                      <span>• Reorder Quantity</span>
                      <span>• Manufacturer Lead Time</span>
                      <span>• Shipping Lead Time</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Select File</label>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                      data-testid="bulk-import-file-input"
                    />
                  </div>

                  {bulkImportFile && (
                    <div className="text-sm text-muted-foreground">
                      Selected: {bulkImportFile.name}
                    </div>
                  )}

                  {/* Loading state */}
                  {previewLoading && (
                    <div className="flex items-center justify-center py-6">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      <span className="ml-3 text-sm text-muted-foreground">Analyzing file...</span>
                    </div>
                  )}

                  {/* Preview error with retry */}
                  {previewError && !previewLoading && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4">
                      <p className="text-sm font-medium text-red-600 mb-2">Could not preview file</p>
                      <p className="text-sm text-muted-foreground mb-3">{previewError}</p>
                      <div className="flex gap-3">
                        <Button
                          onClick={handleBulkImport}
                          disabled={importing}
                          variant="outline"
                          size="sm"
                          data-testid="import-anyway-button"
                        >
                          {importing ? 'Importing...' : 'Import Anyway'}
                        </Button>
                        <Button
                          onClick={closeBulkImportDialog}
                          variant="ghost"
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {previewData && !previewLoading && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-3">
                          <p className="text-xs text-muted-foreground">New Products</p>
                          <p className="text-2xl font-bold text-green-500">{previewData.new_products}</p>
                        </div>
                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-3">
                          <p className="text-xs text-muted-foreground">Already Exist</p>
                          <p className="text-2xl font-bold text-yellow-500">{previewData.existing_products}</p>
                        </div>
                      </div>

                      {previewData.existing_products > 0 && (
                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" strokeWidth={1.5} />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-yellow-600">
                                {previewData.existing_products} product(s) already exist
                              </p>
                              <p className="text-sm text-muted-foreground mt-1">
                                Do you want to update existing products with the new data from the file?
                              </p>
                              <div className="mt-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={updateExisting}
                                    onChange={(e) => setUpdateExisting(e.target.checked)}
                                    className="rounded border-border"
                                    data-testid="update-existing-checkbox"
                                  />
                                  <span className="text-sm">Yes, update existing products</span>
                                </label>
                              </div>
                              {previewData.existing_skus_sample?.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-muted-foreground">Sample existing SKUs:</p>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {previewData.existing_skus_sample.slice(0, 5).map((sku, idx) => (
                                      <span key={`sku-${sku}-${idx}`} className="text-xs bg-background px-2 py-0.5 rounded border border-border font-mono">
                                        {sku}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-3">
                        <Button
                          onClick={handleBulkImport}
                          disabled={importing}
                          data-testid="import-products-button"
                          className="flex-1"
                        >
                          {importing ? (
                            <span className="animate-pulse">Importing...</span>
                          ) : (
                            `Import ${previewData.new_products} New${updateExisting && previewData.existing_products > 0 ? ` + Update ${previewData.existing_products}` : ''} Products`
                          )}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={closeBulkImportDialog}
                          disabled={importing}
                          data-testid="cancel-import-button"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {!previewData && !previewLoading && !previewError && !bulkImportFile && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Upload an Excel (.xlsx) or CSV file to preview the import
                    </p>
                  )}
                </>
              ) : (
                <>
                  {/* Import Results */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-center gap-2 text-green-500">
                      <CheckCircle className="w-8 h-8" strokeWidth={1.5} />
                      <span className="text-lg font-medium">Import Complete</span>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-3 text-center">
                        <p className="text-xs text-muted-foreground">Created</p>
                        <p className="text-2xl font-bold text-green-500">{importResult.created}</p>
                      </div>
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-3 text-center">
                        <p className="text-xs text-muted-foreground">Updated</p>
                        <p className="text-2xl font-bold text-blue-500">{importResult.updated}</p>
                      </div>
                      <div className="bg-muted/50 border border-border rounded-sm p-3 text-center">
                        <p className="text-xs text-muted-foreground">Skipped</p>
                        <p className="text-2xl font-bold">{importResult.skipped}</p>
                      </div>
                    </div>

                    <div className="bg-muted/30 border border-border rounded-sm p-4">
                      <p className="text-sm">
                        <strong>{importResult.created}</strong> new products were added and{' '}
                        <strong>{importResult.updated}</strong> existing products were updated.
                        {importResult.skipped > 0 && (
                          <> <strong>{importResult.skipped}</strong> products were skipped (already existed, update not selected).</>
                        )}
                      </p>
                    </div>

                    {importResult.errors?.length > 0 && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4">
                        <p className="text-sm font-medium text-red-600 mb-2">Errors ({importResult.errors.length}):</p>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {importResult.errors.map((err, idx) => (
                            <li key={`err-${idx}`}>• {err}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <Button
                      onClick={closeBulkImportDialog}
                      className="w-full"
                      data-testid="close-import-result-button"
                    >
                      Done
                    </Button>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Update Inventory Dialog */}
        <Dialog open={inventoryDialogOpen} onOpenChange={closeInventoryDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle data-testid="update-inventory-dialog-title">Update Central Warehouse Inventory</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {!inventoryResult ? (
                <>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-4">
                    <p className="text-sm font-medium text-blue-600 mb-2">What this does:</p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Updates <strong>ONLY the inventory quantity</strong> for existing products</li>
                      <li>• All other product fields remain unchanged</li>
                      <li>• SKUs not found in the system will be skipped</li>
                    </ul>
                  </div>

                  <div className="bg-muted/30 border border-border rounded-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium">Required Columns:</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={downloadInventoryTemplate}
                        data-testid="download-inventory-template-button"
                      >
                        <Download className="w-3 h-3 mr-1" strokeWidth={1.5} />
                        Download Template
                      </Button>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <span>• SKU (required)</span>
                      <span className="mx-2">•</span>
                      <span>• Inventory / Stock / Quantity (required)</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Select File</label>
                    <Input
                      ref={inventoryFileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleInventoryFileSelect}
                      data-testid="inventory-file-input"
                    />
                  </div>

                  {inventoryFile && (
                    <div className="text-sm text-muted-foreground">
                      Selected: <span className="font-mono">{inventoryFile.name}</span>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      onClick={handleInventoryUpdate}
                      disabled={!inventoryFile || updatingInventory}
                      data-testid="update-inventory-submit-button"
                      className="flex-1"
                    >
                      {updatingInventory ? (
                        <span className="animate-pulse">Updating...</span>
                      ) : (
                        'Update Inventory'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={closeInventoryDialog}
                      disabled={updatingInventory}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Update Results */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-center gap-2 text-green-500">
                      <CheckCircle className="w-8 h-8" strokeWidth={1.5} />
                      <span className="text-lg font-medium">Inventory Updated</span>
                    </div>

                    <div className="bg-muted/30 rounded-sm p-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Products Updated:</span>
                          <span className="ml-2 font-bold text-green-600">{inventoryResult.updated}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Total Rows:</span>
                          <span className="ml-2 font-bold">{inventoryResult.total_rows}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">SKUs Not Found:</span>
                          <span className="ml-2 font-bold text-orange-500">{inventoryResult.skipped_not_found}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Invalid Rows:</span>
                          <span className="ml-2 font-bold text-muted-foreground">{inventoryResult.skipped_invalid}</span>
                        </div>
                      </div>
                    </div>

                    {inventoryResult.errors?.length > 0 && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4">
                        <p className="text-sm font-medium text-red-600 mb-2">Errors:</p>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {inventoryResult.errors.map((err, idx) => (
                            <li key={`err-${idx}`}>• {err}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground text-center">
                      {inventoryResult.note}
                    </p>

                    <Button
                      onClick={closeInventoryDialog}
                      className="w-full"
                      data-testid="close-inventory-result-button"
                    >
                      Done
                    </Button>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Update Parents Dialog */}
        <Dialog open={colorParentDialogOpen} onOpenChange={closeColorParentDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle data-testid="update-parents-dialog-title">Update Parents (for SKU Grouping)</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {!colorParentResult ? (
                <>
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-sm p-4">
                    <p className="text-sm font-medium text-purple-600 mb-2">What this does:</p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Updates <strong>Parent SKU</strong> (groups all colors/sizes of a style)</li>
                      <li>• Updates <strong>Color Parent</strong> (groups sizes of same color)</li>
                      <li>• MOQ will apply to the total of all SKUs with the same color parent</li>
                    </ul>
                  </div>

                  <div className="bg-muted/30 border border-border rounded-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium">Template Columns:</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={downloadColorParentTemplate}
                        data-testid="download-parents-template-button"
                      >
                        <Download className="w-3 h-3 mr-1" strokeWidth={1.5} />
                        Download Template
                      </Button>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <p><strong>SKU</strong> | <strong>Parent SKU</strong> | <strong>Color Parent</strong> | <strong>Vendor</strong></p>
                      <p className="mt-1 text-xs">Fill in Parent SKU for style grouping, Color Parent for size grouping.</p>
                      <p className="text-xs">Example: RS11-RED_S, RS11-RED_M → Parent SKU: RS11, Color Parent: RS11-RED</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Select Updated File</label>
                    <Input
                      ref={colorParentFileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleColorParentFileSelect}
                      data-testid="parents-file-input"
                    />
                  </div>

                  {colorParentFile && (
                    <div className="text-sm text-muted-foreground">
                      Selected: <span className="font-mono">{colorParentFile.name}</span>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      onClick={handleColorParentUpdate}
                      disabled={!colorParentFile || updatingColorParent}
                      data-testid="update-parents-submit-button"
                      className="flex-1"
                    >
                      {updatingColorParent ? (
                        <span className="animate-pulse">Updating...</span>
                      ) : (
                        'Update Parents'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={closeColorParentDialog}
                      disabled={updatingColorParent}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Update Results */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-center gap-2 text-green-500">
                      <CheckCircle className="w-8 h-8" strokeWidth={1.5} />
                      <span className="text-lg font-medium">Color Parents Updated</span>
                    </div>

                    <div className="bg-muted/30 rounded-sm p-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Products Updated:</span>
                          <span className="ml-2 font-bold text-green-600">{colorParentResult.updated}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Total Rows:</span>
                          <span className="ml-2 font-bold">{colorParentResult.total_rows}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">SKUs Not Found:</span>
                          <span className="ml-2 font-bold text-orange-500">{colorParentResult.skipped_not_found}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Empty Values:</span>
                          <span className="ml-2 font-bold text-muted-foreground">{colorParentResult.skipped_empty}</span>
                        </div>
                      </div>
                    </div>

                    {colorParentResult.errors?.length > 0 && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 max-h-32 overflow-y-auto">
                        <p className="text-sm font-medium text-red-600 mb-2">Errors ({colorParentResult.errors.length}):</p>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {colorParentResult.errors.map((err, idx) => (
                            <li key={`err-${idx}`}>• {err}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground text-center">
                      Products with the same Color Parent will now be grouped together in Reorder recommendations.
                    </p>

                    <Button
                      onClick={closeColorParentDialog}
                      className="w-full"
                      data-testid="close-color-parent-result-button"
                    >
                      Done
                    </Button>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Dialog */}
        <Dialog open={bulkEditDialogOpen} onOpenChange={closeBulkEditDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle data-testid="bulk-edit-dialog-title">Edit Products via File</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {!bulkEditResult ? (
                <>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-4">
                    <p className="text-sm font-medium text-blue-600 mb-2">How it works:</p>
                    <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                      <li>Download your products using "Download All" button</li>
                      <li>Edit the CSV in Excel/Sheets (do not change SKU column)</li>
                      <li>Upload the edited file here to preview and apply changes</li>
                    </ol>
                    <p className="text-xs text-muted-foreground mt-2">
                      SKU and Inventory columns cannot be edited through this workflow.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Upload Edited File</label>
                    <Input
                      ref={bulkEditFileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleBulkEditFileSelect}
                      data-testid="bulk-edit-file-input"
                    />
                  </div>

                  {bulkEditFile && (
                    <p className="text-sm text-muted-foreground">
                      Selected: <span className="font-mono">{bulkEditFile.name}</span>
                    </p>
                  )}

                  {bulkEditPreviewLoading && (
                    <div className="flex items-center justify-center py-6">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      <span className="ml-3 text-sm text-muted-foreground">Comparing changes...</span>
                    </div>
                  )}

                  {bulkEditPreview && !bulkEditPreviewLoading && (
                    <div className="space-y-4">
                      {/* Summary */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-3 text-center">
                          <p className="text-xs text-muted-foreground">Products Modified</p>
                          <p className="text-2xl font-bold text-blue-600" data-testid="preview-modified-count">
                            {bulkEditPreview.products_with_changes}
                          </p>
                        </div>
                        <div className="bg-muted/50 border border-border rounded-sm p-3 text-center">
                          <p className="text-xs text-muted-foreground">Unchanged</p>
                          <p className="text-2xl font-bold">{bulkEditPreview.unchanged}</p>
                        </div>
                        <div className={`rounded-sm p-3 text-center ${bulkEditPreview.not_found > 0 ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-muted/50 border border-border'}`}>
                          <p className="text-xs text-muted-foreground">Not Found</p>
                          <p className={`text-2xl font-bold ${bulkEditPreview.not_found > 0 ? 'text-amber-600' : ''}`}>
                            {bulkEditPreview.not_found}
                          </p>
                        </div>
                      </div>

                      {/* Changes Detail */}
                      {bulkEditPreview.products_with_changes > 0 ? (
                        <div className="border border-border rounded-sm">
                          <div className="bg-muted/30 p-3 border-b border-border">
                            <p className="text-sm font-medium">Changes Preview</p>
                          </div>
                          <div className="max-h-64 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-muted/20 sticky top-0">
                                <tr>
                                  <th className="text-left p-2 text-xs font-medium">SKU</th>
                                  <th className="text-left p-2 text-xs font-medium">Field</th>
                                  <th className="text-left p-2 text-xs font-medium">Old Value</th>
                                  <th className="text-left p-2 text-xs font-medium">New Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {bulkEditPreview.changes.slice(0, 50).map((item, i) =>
                                  item.changes.map((change, j) => (
                                    <tr key={`${i}-${j}`} className="border-b border-border/50 hover:bg-muted/20">
                                      <td className="p-2 font-mono text-xs">{j === 0 ? item.sku : ''}</td>
                                      <td className="p-2 text-xs">{change.field_label}</td>
                                      <td className="p-2 text-xs text-muted-foreground line-through">{change.old_value}</td>
                                      <td className="p-2 text-xs font-medium text-blue-600">{change.new_value}</td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                            {bulkEditPreview.changes.length > 50 && (
                              <p className="p-2 text-xs text-muted-foreground text-center">
                                ...and {bulkEditPreview.changes.length - 50} more products
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-muted/30 border border-border rounded-sm p-6 text-center">
                          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" strokeWidth={1.5} />
                          <p className="text-sm text-muted-foreground">No changes detected. All values match.</p>
                        </div>
                      )}

                      {bulkEditPreview.not_found > 0 && (
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-3">
                          <p className="text-xs text-amber-600 font-medium mb-1">
                            {bulkEditPreview.not_found} SKU(s) not found in system:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {bulkEditPreview.not_found_skus.slice(0, 10).map((sku, idx) => (
                              <span key={idx} className="text-xs bg-background px-2 py-0.5 rounded border border-border font-mono">{sku}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex gap-3">
                        <Button
                          onClick={handleBulkEditApply}
                          disabled={bulkEditApplying || bulkEditPreview.products_with_changes === 0}
                          data-testid="apply-bulk-edit-button"
                          className="flex-1"
                        >
                          {bulkEditApplying ? (
                            <span className="animate-pulse">Applying...</span>
                          ) : (
                            `Apply ${bulkEditPreview.products_with_changes} Change(s)`
                          )}
                        </Button>
                        <Button variant="outline" onClick={closeBulkEditDialog} disabled={bulkEditApplying}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {!bulkEditPreview && !bulkEditPreviewLoading && !bulkEditFile && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Upload your edited CSV or Excel file to see a preview of changes
                    </p>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-2 text-green-500">
                    <CheckCircle className="w-8 h-8" strokeWidth={1.5} />
                    <span className="text-lg font-medium">Edit Complete</span>
                  </div>
                  <div className="bg-muted/30 rounded-sm p-4 text-center">
                    <p className="text-sm">
                      <strong>{bulkEditResult.updated}</strong> products updated successfully.
                      {bulkEditResult.not_found > 0 && (
                        <> <strong>{bulkEditResult.not_found}</strong> SKUs not found.</>
                      )}
                    </p>
                  </div>
                  {bulkEditResult.errors?.length > 0 && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-3">
                      <p className="text-xs text-red-600 font-medium mb-1">Errors:</p>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        {bulkEditResult.errors.map((err, idx) => (
                          <li key={`err-${idx}`}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <Button onClick={closeBulkEditDialog} className="w-full" data-testid="close-bulk-edit-result-button">
                    Done
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}